import { CloseOutlined } from '@mui/icons-material'
import { Box, DialogTitle, IconButton } from '@mui/material'
import { ReactElement, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

export interface PopupTitleProps {
    children: ReactNode
    /** id on the title (an `h2`): aria-labelledby target, and the drag handle when draggable */
    id?: string
    /** omit to render no close button */
    onClose?: () => void
    /** shows the move cursor; the Paper's Draggable must use this title's `id` as its handle */
    draggable?: boolean
    /** id on the span around the ✕ icon — what most E2E selectors click (`//span[@id=…]`) */
    closeId?: string
    /** id on the ✕ button itself, for selectors that target the button */
    closeButtonId?: string
    /**
     * A native `title` on the ✕ span. Only for popups whose E2E selector matches on it
     * (API Token: `//span[@id='close-icon' and @title='Close']`); prefer the aria-label otherwise.
     */
    closeTitle?: string
}

/**
 * The one popup title row (UBQ-5808 / UBQ-5844): the theme's `MuiDialogTitle` (16px, 600, teal)
 * with a ✕ button pinned top-right. Use it instead of a hand-built `DialogTitle` + flex `Box` +
 * `Typography variant="h6"` + `Icon`, so every popup's title and close button look and sit alike.
 */
const PopupTitle = ({
    children,
    id,
    onClose,
    draggable,
    closeId,
    closeButtonId,
    closeTitle,
}: PopupTitleProps): ReactElement => {
    const { t } = useTranslation()
    return (
        <>
            <DialogTitle
                id={id}
                sx={{
                    ...(onClose && { pr: 7 }),
                    ...(draggable && { cursor: 'move' }),
                }}
            >
                {children}
            </DialogTitle>
            {onClose && (
                <IconButton
                    id={closeButtonId}
                    onClick={onClose}
                    aria-label={t('close')}
                    size="small"
                    sx={{ position: 'absolute', right: 12, top: 12 }}
                >
                    <Box
                        component="span"
                        id={closeId}
                        title={closeTitle}
                        sx={{ display: 'flex' }}
                    >
                        <CloseOutlined fontSize="small" />
                    </Box>
                </IconButton>
            )}
        </>
    )
}

export default PopupTitle
